import { CurrencyMarkup } from '../types/Package';

export interface CurrencyConversionResult {
  baseAmount: number;
  markupAmount: number;
  markupPercentage: number;
  rounded: boolean;
  exchangeRate: number;
  finalRate: number;
  roundToNearest?: number;
}

export interface CurrencySettings {
  [currency: string]: CurrencyMarkup;
}

// Default currency markup settings
export const DEFAULT_CURRENCY_SETTINGS: CurrencySettings = {
  'USD': { markupPercentage: 15, roundToNearest: 10, enabled: true },
  'EUR': { markupPercentage: 15, roundToNearest: 10, enabled: true },
  'GBP': { markupPercentage: 15, roundToNearest: 10, enabled: true },
  'CAD': { markupPercentage: 15, roundToNearest: 10, enabled: true },
  'AUD': { markupPercentage: 15, roundToNearest: 10, enabled: true }
};

// Currency symbols mapping
export const CURRENCY_SYMBOLS: { [key: string]: string } = {
  'R': 'R',
  'ZAR': 'R', // South African Rand - use R symbol
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'CAD': 'C$',
  'AUD': 'A$'
};

// Country to currency mapping
export const COUNTRY_CURRENCY_MAP: { [key: string]: string } = {
  // USD countries
  'US': 'USD',
  'PR': 'USD', // Puerto Rico
  'VI': 'USD', // US Virgin Islands
  'GU': 'USD', // Guam
  'AS': 'USD', // American Samoa
  'MP': 'USD', // Northern Mariana Islands
  
  // GBP countries
  'GB': 'GBP',
  'IM': 'GBP', // Isle of Man
  'JE': 'GBP', // Jersey
  'GG': 'GBP', // Guernsey
  
  // EUR countries
  'DE': 'EUR', // Germany
  'FR': 'EUR', // France
  'IT': 'EUR', // Italy
  'ES': 'EUR', // Spain
  'NL': 'EUR', // Netherlands
  'LU': 'EUR', // Luxembourg
  'BE': 'EUR', // Belgium
  'AT': 'EUR', // Austria
  'PT': 'EUR', // Portugal
  'GR': 'EUR', // Greece
  'FI': 'EUR', // Finland
  'IE': 'EUR', // Ireland
  'SI': 'EUR', // Slovenia
  'SK': 'EUR', // Slovakia
  'EE': 'EUR', // Estonia
  'LV': 'EUR', // Latvia
  'LT': 'EUR', // Lithuania
  'MT': 'EUR', // Malta
  'CY': 'EUR', // Cyprus
  'AD': 'EUR', // Andorra
  'MC': 'EUR', // Monaco
  'SM': 'EUR', // San Marino
  'VA': 'EUR', // Vatican City
  
  // CAD countries
  'CA': 'CAD',
  
  // AUD countries
  'AU': 'AUD',
  'NZ': 'AUD', // New Zealand (uses AUD for simplicity, though NZD exists)
  
  // ZAR countries
  'ZA': 'R' // South Africa
};

// Load currency settings from localStorage
export const loadCurrencySettings = (): CurrencySettings => {
  try {
    const saved = localStorage.getItem('currency-settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_CURRENCY_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('Error loading currency settings:', error);
  }
  return DEFAULT_CURRENCY_SETTINGS;
};

// Save currency settings to localStorage
export const saveCurrencySettings = (settings: CurrencySettings): void => {
  try {
    localStorage.setItem('currency-settings', JSON.stringify(settings));
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('currency_settings_updated', { detail: settings }));
  } catch (error) {
    console.error('Error saving currency settings:', error);
  }
};

// Detect user's currency based on IP location
export const detectUserCurrency = async (): Promise<string> => {
  try {
    // Try IP-based detection first with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    const data = await response.json();
    
    console.log('🌍 IP Geolocation API response:', {
      country_code: data.country_code,
      country_name: data.country_name,
      city: data.city,
      currency: data.currency
    });
    
    if (data.country_code) {
      // Check if country is in our map
      if (COUNTRY_CURRENCY_MAP[data.country_code]) {
        const currency = COUNTRY_CURRENCY_MAP[data.country_code];
        console.log(`🌍 Country ${data.country_code} (${data.country_name}) mapped to ${currency}`);
        return currency;
      }
      
      // Handle unmapped countries: ZA uses R, all others use USD
      if (data.country_code === 'ZA') {
        console.log(`🌍 Country ${data.country_code} (${data.country_name}) defaulting to R`);
        return 'R';
      }
      
      // Default to USD for all other unmapped countries
      console.log(`🌍 Country ${data.country_code} (${data.country_name}) not in currency map, defaulting to USD`);
      return 'USD';
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn('IP-based currency detection timed out, using fallback');
    } else {
      console.warn('IP-based currency detection failed:', error);
    }
  }

  try {
    // Fallback to browser locale
    const locale = navigator.language || navigator.languages[0];
    const localeCurrencyMap: { [key: string]: string } = {
      // USD locales
      'en-US': 'USD',
      'es-PR': 'USD', // Puerto Rico
      
      // CAD locales
      'en-CA': 'CAD',
      'fr-CA': 'CAD',
      
      // GBP locales
      'en-GB': 'GBP',
      
      // AUD locales
      'en-AU': 'AUD',
      'en-NZ': 'AUD', // New Zealand
      
      // ZAR locales
      'en-ZA': 'R',
      'af-ZA': 'R',
      
      // EUR locales
      'de-DE': 'EUR', // Germany
      'fr-FR': 'EUR', // France
      'it-IT': 'EUR', // Italy
      'es-ES': 'EUR', // Spain
      'nl-NL': 'EUR', // Netherlands
      'fr-LU': 'EUR', // Luxembourg (French)
      'de-LU': 'EUR', // Luxembourg (German)
      'lb-LU': 'EUR', // Luxembourg (Luxembourgish)
      'fr-BE': 'EUR', // Belgium (French)
      'nl-BE': 'EUR', // Belgium (Dutch)
      'de-AT': 'EUR', // Austria
      'pt-PT': 'EUR', // Portugal
      'el-GR': 'EUR', // Greece
      'fi-FI': 'EUR', // Finland
      'ga-IE': 'EUR', // Ireland (Irish)
      'en-IE': 'EUR', // Ireland (English)
      'sl-SI': 'EUR', // Slovenia
      'sk-SK': 'EUR', // Slovakia
      'et-EE': 'EUR', // Estonia
      'lv-LV': 'EUR', // Latvia
      'lt-LT': 'EUR', // Lithuania
      'mt-MT': 'EUR', // Malta
      'el-CY': 'EUR', // Cyprus
      'ca-AD': 'EUR', // Andorra
      'fr-MC': 'EUR', // Monaco
      'it-SM': 'EUR', // San Marino
      'it-VA': 'EUR'  // Vatican City
    };
    
    if (localeCurrencyMap[locale]) {
      return localeCurrencyMap[locale];
    }
  } catch (error) {
    console.warn('Locale-based currency detection failed:', error);
  }

  // Default fallback: USD for all unmapped countries (except ZA which uses R)
  return 'USD';
};

// Get exchange rate from free API
export const getExchangeRate = async (fromCurrency: string, toCurrency: string): Promise<number> => {
  try {
    // Map "R" to "ZAR" for API calls (API uses ISO currency codes)
    const apiFromCurrency = fromCurrency === 'R' ? 'ZAR' : fromCurrency;
    const apiToCurrency = toCurrency === 'R' ? 'ZAR' : toCurrency;
    
    // Use exchangerate-api.com (free tier) with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${apiFromCurrency}`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    const data = await response.json();
    
    if (data.rates && data.rates[apiToCurrency]) {
      return data.rates[apiToCurrency];
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn('Exchange rate API timed out, using fallback rates');
    } else {
      console.error('Exchange rate API failed:', error);
    }
  }

  // Fallback to hardcoded rates (approximate)
  const fallbackRates: { [key: string]: { [key: string]: number } } = {
    'R': { 'USD': 0.055, 'EUR': 0.050, 'GBP': 0.043, 'CAD': 0.075, 'AUD': 0.082 },
    'USD': { 'R': 18.2, 'EUR': 0.91, 'GBP': 0.79, 'CAD': 1.35, 'AUD': 1.48 },
    'EUR': { 'R': 20.0, 'USD': 1.10, 'GBP': 0.87, 'CAD': 1.48, 'AUD': 1.63 },
    'GBP': { 'R': 23.0, 'USD': 1.27, 'EUR': 1.15, 'CAD': 1.70, 'AUD': 1.87 },
    'CAD': { 'R': 13.5, 'USD': 0.74, 'EUR': 0.68, 'GBP': 0.59, 'AUD': 1.10 },
    'AUD': { 'R': 12.2, 'USD': 0.68, 'EUR': 0.61, 'GBP': 0.53, 'CAD': 0.91 }
  };

  return fallbackRates[fromCurrency]?.[toCurrency] || 1;
};

// Convert currency with markup and rounding
export const convertCurrencyWithMarkup = async (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  markupSettings: CurrencySettings = DEFAULT_CURRENCY_SETTINGS
): Promise<CurrencyConversionResult> => {
  try {
    console.log(`💰 Converting ${amount} ${fromCurrency} to ${toCurrency}`);
    console.log('💰 Markup settings:', markupSettings);
    
    // Get base exchange rate (using cached version - once per day)
    const baseRate = await getCachedExchangeRate(fromCurrency, toCurrency);
    console.log(`💰 Base exchange rate: ${baseRate}`);
    
    // Apply markup if configured
    const markupConfig = markupSettings[toCurrency];
    let finalRate = baseRate;
    let markupPercentage = 0;
    
    if (markupConfig && markupConfig.enabled) {
      const markupMultiplier = 1 + (markupConfig.markupPercentage / 100);
      finalRate = baseRate * markupMultiplier;
      markupPercentage = markupConfig.markupPercentage;
      console.log(`💰 Applied ${markupPercentage}% markup: ${baseRate} -> ${finalRate}`);
    }
    
    // Calculate amounts
    const baseAmount = amount * baseRate;
    let markupAmount = amount * finalRate;
    console.log(`💰 Base amount: ${baseAmount}, Markup amount: ${markupAmount}`);
    
    // Apply rounding if configured (round UP)
    let rounded = false;
    if (markupConfig && markupConfig.roundToNearest) {
      const roundTo = markupConfig.roundToNearest;
      const beforeRound = markupAmount;
      markupAmount = Math.ceil(markupAmount / roundTo) * roundTo;
      rounded = true;
      console.log(`💰 Rounded UP from ${beforeRound} to ${markupAmount} (nearest ${roundTo})`);
    }
    
    const result = {
      baseAmount: Math.round(baseAmount * 100) / 100,
      markupAmount: Math.round(markupAmount * 100) / 100,
      markupPercentage,
      rounded,
      exchangeRate: baseRate,
      finalRate,
      roundToNearest: markupConfig?.roundToNearest
    };
    
    console.log('💰 Final conversion result:', result);
    return result;
  } catch (error) {
    console.error('Currency conversion failed:', error);
    return {
      baseAmount: amount,
      markupAmount: amount,
      markupPercentage: 0,
      rounded: false,
      exchangeRate: 1,
      finalRate: 1
    };
  }
};

// Format currency display with thousand separators and no decimals
export const formatCurrency = (amount: number, currency: string): string => {
  // Normalize ZAR to R for display
  const normalizedCurrency = currency === 'ZAR' ? 'R' : currency;
  const symbol = CURRENCY_SYMBOLS[normalizedCurrency] || normalizedCurrency;
  // Round to nearest integer and format with thousand separators
  const roundedAmount = Math.round(amount);
  const formattedAmount = roundedAmount.toLocaleString('en-US');
  return `${symbol} ${formattedAmount}`;
};

// Format addon price with optional "from" prefix
export const formatAddonPrice = (amount: number, currency: string, showFrom: boolean = false): string => {
  const formatted = formatCurrency(amount, currency);
  return showFrom ? `from ${formatted}` : formatted;
};

// Get currency symbol
export const getCurrencySymbol = (currency: string): string => {
  return CURRENCY_SYMBOLS[currency] || currency;
};

// Cache exchange rates to reduce API calls - once per day
const exchangeRateCache: { [key: string]: { rate: number; date: string } } = {};
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (once per day)

export const getCachedExchangeRate = async (fromCurrency: string, toCurrency: string): Promise<number> => {
  // Use date-based cache key to ensure daily refresh
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  const cacheKey = `${fromCurrency}_${toCurrency}_${today}`;
  const cached = exchangeRateCache[cacheKey];
  
  // Check if we have a cached rate for today
  if (cached && cached.date === today) {
    console.log(`💰 Using cached exchange rate for ${fromCurrency} to ${toCurrency} (from ${today})`);
    return cached.rate;
  }
  
  // Fetch new rate and cache it with today's date
  console.log(`💰 Fetching new exchange rate for ${fromCurrency} to ${toCurrency} (date: ${today})`);
  const rate = await getExchangeRate(fromCurrency, toCurrency);
  exchangeRateCache[cacheKey] = { rate, date: today };
  
  // Clean up old cache entries (older than 2 days)
  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
  const cutoffDate = twoDaysAgo.toISOString().split('T')[0];
  
  Object.keys(exchangeRateCache).forEach(key => {
    if (exchangeRateCache[key].date < cutoffDate) {
      delete exchangeRateCache[key];
    }
  });
  
  return rate;
};
